import {FC, useState} from 'react';
import {Box, Typography} from '@mui/material';
import Dropdown from '@/shared/components/Dropdown';
import {BODY1, CANCEL, DELETE, EDIT, ERROR, IMAGE_PREVIEW_ALT} from '@/utils/constants';
import {ReactComponent as More} from '@icons/more-vertical.svg';
import {ReactComponent as DangerIcon} from '@icons/danger.svg';
import {donorsListStyle} from '../../AuctionItem/FormContent/AuctionDonors/DonorsList/DonorsList.styles';
import {IOrganizationAuctionDonor} from '@/utils/types';
import Modal from '@/shared/components/Modal';
import {OrganizationAuctionService} from '@/utils/services';
import {getAuction, getAuctionLoaderSelector} from '@/store/organization-auction';
import {useAppDispatch} from '@/store/hooks';
import {useSelector} from 'react-redux';
import Loader from '@/shared/components/Loader';
import AddDonorModalForm from '../../AuctionItem/FormContent/AuctionDonors/AddDonorModalForm';
import {orgAPI} from '@/core/api';
import {useApiInstance} from '@/contexts/ApiInstanceContext';

interface DonorsListProps {
	isModalOpen: boolean;
	onModalChange: () => void;
	donors: IOrganizationAuctionDonor[] | null;
	id: number | null;
}

const AuctionDetailsDonor: FC<DonorsListProps> = ({isModalOpen, onModalChange, donors, id}) => {
	const dispatch = useAppDispatch();
	const isLoading = useSelector(getAuctionLoaderSelector);

	const {apiInstance} = useApiInstance();

	const [isDropdownActionsOpen, setIsDropdownActionsOpen] = useState(false);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

	const [targetDonorId, setTargetDonorId] = useState<number | null>(null);
	const [editableDonor, setEditableDonor] = useState<any | null>(null);

	const handleDropdownButtonOpen = (id?: number | null) => () => {
		setIsDropdownActionsOpen(!isDropdownActionsOpen);
		if (id) setTargetDonorId(id);
	};

	const handleDeleteModalOpen = () => {
		setIsDeleteModalOpen(!isDeleteModalOpen);
	};

	const handleDeleteAuctionDonor = async () => {
		if (!targetDonorId || !id) return;
		await OrganizationAuctionService.deleteDonor(targetDonorId);
		await dispatch(getAuction({id, apiInstance})).unwrap();

		setTargetDonorId(null);
		setIsDeleteModalOpen(false);
	};

	return (
		<Box sx={donorsListStyle.wrapper}>
			{isLoading ? (
				<Loader />
			) : (
				donors?.map((donor) => {
					return (
						<Box sx={donorsListStyle.content} key={donor.id}>
							<Box key={donor.id} sx={donorsListStyle.donor}>
								<Box sx={donorsListStyle.donorImage}>
									<img src={donor.logoUrl} alt={IMAGE_PREVIEW_ALT} />
								</Box>

								<Box key={donor.id} sx={donorsListStyle.donorName}>
									{donor.name}
								</Box>

								<Dropdown
									parentClassName={donorsListStyle.dropdown}
									toggleContent={
										<Box>
											<More />
										</Box>
									}
									options={
										<Box>
											<Typography
												variant={BODY1}
												sx={donorsListStyle.dropdownOption}
												onClick={() => {
													setEditableDonor(donor);
													onModalChange();
												}}
											>
												{EDIT}
											</Typography>
											<Typography
												variant={BODY1}
												sx={donorsListStyle.dropdownOption}
												onClick={() => {
													setIsDeleteModalOpen(true);
												}}
											>
												{DELETE}
											</Typography>
										</Box>
									}
									open={targetDonorId === donor.id && isDropdownActionsOpen}
									onOpen={handleDropdownButtonOpen(donor.id)}
									onClose={handleDropdownButtonOpen()}
								/>
							</Box>
						</Box>
					);
				})
			)}

			<Modal
				title="Delete Donor"
				description="Are you sure you want to delete donor?"
				closeButtonTitle={CANCEL}
				mainButtonTitle={DELETE}
				onClose={handleDeleteModalOpen}
				onSave={handleDeleteAuctionDonor}
				open={isDeleteModalOpen}
				mainIcon={<DangerIcon />}
				sx={donorsListStyle.modal}
				color={ERROR}
				isShowCloseButton
				isShowSaveButton
			/>
			{isModalOpen && <AddDonorModalForm onClose={onModalChange} editableDonor={editableDonor} isEditMode={false} />}
		</Box>
	);
};

export default AuctionDetailsDonor;
